import adapter from 'webrtc-adapter';
import type { JanusJS } from 'janus-1';
import { default as Janus } from 'janus-1';
import type { Room, Publisher } from './entities';
import { seconds } from '@/utils';

export interface PlayOptions {
    server: string;
    //name of the stream to play
    name: string;
    token: string;
    attempt?: number;
    debug?: boolean;
}

export class Player {
    constructor(options: PlayOptions) {
        this.server = options.server;
        this.streamName = options.name;
        this.token = options.token;
        this.attempt = options.attempt ?? 1;
        this.debug = options.debug ?? false;
    }

    server: string;
    streamName: string;
    token: string;
    attempt: number = 1;
    debug: boolean = false;

    private _state = 'constructing';

    public opaqueId = `vr-${Janus.randomString(12)}`;

    janus?: Janus;
    roomPlugin?: JanusJS.PluginHandle;
    room: Room | undefined;
    publisherPlugin?: JanusJS.PluginHandle;

    private connectionState: {
        ice: string;
        webrtc: boolean;
        resolver?: () => void;
    } = { ice: 'unknown', webrtc: false };

    //events to subscribe on...
    private addLog(item: { event: string; [rest: string]: any }) {
        if (this.log) this.log(item);
    }
    public log(item: { event: string; [rest: string]: any }) {}
    public onError(message: string) {}
    public onStateChange(newState: string) {}
    public onTwitchStart() {}
    public onTwitchEnd() {}

    private stream?: MediaStream;

    public async start() {
        try {
            //initialize Janus..
            await this.init();

            //connect to the websocket..
            this.janus = await this.connect();

            // now we need to emit this event now to comply with the flow of the other
            // transport types so to speak...
            this.state = 'connected';
            //attach the plugin...
            this.roomPlugin = await this.attachRoom();

            this.room = await this.joinRoom();

            this.publisherPlugin = await this.attachPublisher();
            let jsep = await this.joinPublisher(this.room.publishers[0], this.room.private_id);
            jsep = await this.answer(jsep);

            await this.startFeed(jsep);
            await this.rightConnectionStates();

            this.state = 'streaming';
        } catch (error) {
            //just to make sure the error is not registered during the 'destroying_janus' state
            setTimeout(this.destroy.bind(this));
            console.log(`throw that ${error}`);
            throw error;
        }

        return this.stream!;
    }

    async init() {
        this.state = 'initializing';
        const dependencies = Janus.useDefaultDependencies({ adapter });
        return new Promise(resolve => {
            Janus.init({
                debug: this.debug,
                dependencies,
                callback: resolve
            });
        });
    }

    //create the Janus session, connecting to the signaling websocket
    async connect(): Promise<Janus> {
        this.state = 'connecting';
        return new Promise((resolve, reject) => {
            const janus = new Janus({
                server: `wss://${this.server}/socket`,
                success: () => resolve(janus),
                error: (error: any) => {
                    if (this.state === 'connecting') {
                        reject(error);
                    } else {
                        this.addLog({ event: 'connectionError', error });
                    }
                },
                destroyed: () => {
                    this.addLog({ event: 'JanusDestroyed' });
                    this.destroy();
                },
                iceServers: [],
                token: this.token
            });
        });
    }

    async attachRoom(): Promise<JanusJS.PluginHandle> {
        this.state = 'attaching_room';
        return new Promise((resolve, reject) => {
            this.janus!.attach({
                success: (plugin: any) => resolve(plugin),
                plugin: 'janus.plugin.videoroom',
                opaqueId: this.opaqueId,
                error: (error: any) => {
                    reject(error);
                },
                consentDialog: (on: boolean) => {
                    this.addLog({ event: 'consentDialog' });
                },
                mediaState: (state: any, on: any) => {
                    this.addLog({ event: 'RoomMediaState', state, on });
                },
                webrtcState: (state: any) => {
                    this.addLog({ event: 'RoomWrtcState', state });
                },
                iceState: (state: any) => {
                    this.addLog({ event: 'RoomIceState', state });
                },
                slowLink: (state: any) => {
                    this.addLog({ event: 'RoomSlowLink', state });
                },
                onmessage: this.onRoomMessage.bind(this),
                onlocaltrack: (track: MediaStreamTrack) => this.addLog({ event: 'LocalStream' }),
                onremotetrack: (track: MediaStreamTrack) => {
                    this.addLog({ event: 'RoomRemoteStream', id: track.id });
                },
                ondataopen: () => this.addLog({ event: 'RoomDataOpen' }),
                ondata: (msg: any) => this.addLog({ event: 'RoomData' }),
                oncleanup: () => this.addLog({ event: 'RoomCleanup' }),
                ondetached: () => this.addLog({ event: 'RoomDetached' })
            });
        });
    }

    async joinRoom(): Promise<Room> {
        this.state = 'joining_room';
        return new Promise((resolve, reject) => {
            this.roomPlugin!.send({
                message: {
                    request: 'join',
                    room: this.streamName,
                    ptype: 'publisher',
                    token: this.token,
                    id: this.token
                }
            });
            this._resolver = { resolve, reject };
            //todo: fix that timeout
        });
    }

    async attachPublisher(): Promise<JanusJS.PluginHandle> {
        this.state = 'attaching_publisher';
        return new Promise((resolve, reject) => {
            this.janus!.attach({
                success: (handle: JanusJS.PluginHandle) => {
                    resolve(handle);
                },
                plugin: 'janus.plugin.videoroom',
                opaqueId: this.opaqueId,
                error: (error: any) => {
                    Janus.error(error);
                    reject(error);
                },
                onmessage: this.onPublisherMessage.bind(this),
                webrtcState: (on: boolean) => {
                    this.addLog({ event: 'PublisherWEBRTCState', on: !!on });
                    this.updateConnectionState({ webrtc: !!on });
                },
                onremotetrack: (track: MediaStreamTrack, mid: string, on: boolean, metadata?: JanusJS.RemoteTrackMetadata) => {
                    this.addLog({
                        event: 'PublisherRemoteStream',
                        id: track.id
                    });
                    this.onTrackEvent(track, metadata);
                },
                onlocaltrack: (track: MediaStreamTrack) => this.addLog({ event: 'PublisherLocalStream' }),
                oncleanup: () => {
                    this.addLog({ event: 'PublisherCleanup' });
                },

                mediaState: (state: any, on: any) => {
                    this.addLog({ event: 'PublisherMediaState', state, on });
                },
                //in the real world the 'disconnected' state doesn't exist, and 'checking' does...
                iceState: (state: 'disconnected' | 'connected' | 'failed' | 'closed') => {
                    this.addLog({ event: 'PublisherIceState', state });
                    this.updateConnectionState({ ice: state });
                },
                slowLink: (uplink: boolean, nacks: number) => {
                    this.addLog({ event: 'PublisherSlowLink', uplink, nacks });
                },
                ondataopen: () => this.addLog({ event: 'PublisherDataOpen' }),
                ondata: (msg: any) => this.addLog({ event: 'PublisherData' }),
                ondetached: () => this.addLog({ event: 'PublisherDetached' })
            });
        });
    }

    async joinPublisher(publisher: Publisher, privateId: number): Promise<JanusJS.JSEP> {
        this.state = 'joining_publisher';
        (this.publisherPlugin as any)['videoCodec'] = publisher.video_codec;
        return new Promise((resolve, reject) => {
            this.publisherPlugin!.send({
                message: {
                    request: 'join',
                    room: this.streamName,
                    ptype: 'subscriber',
                    feed: publisher.id,
                    private_id: privateId,
                    close_pc: true
                }
            });
            this._resolver = { resolve, reject };
        });
    }

    async answer(jsep: JanusJS.JSEP): Promise<JanusJS.JSEP> {
        this.state = 'answering';
        return new Promise((resolve, reject) => {
            this.publisherPlugin!.createAnswer({
                jsep,
                tracks: [],
                success: (jsep: JanusJS.JSEP) => resolve(jsep),
                error: (error: any) => {
                    reject(error);
                }
            });
        });
    }

    async startFeed(jsep: JanusJS.JSEP): Promise<void> {
        this.state = 'starting_feed';
        return new Promise((resolve, reject) => {
            this._resolver = { resolve, reject };
            this.publisherPlugin!.send({
                message: { request: 'start', room: this.streamName },
                jsep
            });
        });
    }

    async rightConnectionStates() {
        this.state = 'awaiting_connection_states';
        return new Promise<void>((resolve, reject) => {
            const { ice, webrtc } = this.connectionState;
            if (ice == 'connected' && webrtc) {
                resolve();
                return;
            }

            this.connectionState.resolver = resolve;

            //make sure this state times out when the right conection states are not reached within 3 seconds
            setTimeout(() => {
                if (this.state !== 'awaiting_connection_states') {
                    return;
                }
                reject('connectionstates_timeout');
            }, seconds(5));
        });
    }

    updateConnectionState(states: { ice?: string; webrtc?: boolean }) {
        if ('ice' in states) {
            this.connectionState.ice = states.ice!;
        }
        if ('webrtc' in states) {
            this.connectionState.webrtc = states.webrtc!;
        }

        const { ice, webrtc, resolver } = this.connectionState;
        if (ice == 'connected' && webrtc && resolver) {
            resolver();
        }
    }

     destroy() {
        //no need to detroy when already destroying_janus..
        if (this._state === 'destroying_janus') {
            return;
        }
        try {
            this.state = 'destroying_janus';

            if (this.room) {
                this.roomPlugin!.send({
                    message: { request: 'leave' }
                }); 
            } 

            if (this.publisherPlugin) {
                this.publisherPlugin.detach({});
                this.publisherPlugin = undefined;
            }
            if (this.roomPlugin) {
                this.roomPlugin.detach({});
                this.roomPlugin = undefined;
            }

            if (this.janus) {
                this.janus.destroy({ unload: true });
            }

            this.log = this.onError = this.onStateChange = this.onTwitchEnd = this.onTwitchStart = () => {};
        } catch (error) {
            if (error instanceof Error) {
                this.addLog({
                    event: 'destroyError',
                    message: `${error.name} ${error.message}`
                });
            } else if (typeof error === 'string') {
                this.addLog({ event: 'destroyError', message: error });
            } else if (typeof error === 'object') {
                this.addLog({ ...{ event: 'destroyError' }, ...error });
            } else {
                this.addLog({
                    event: 'destroyError',
                    message: 'General Error'
                });
            }
        }
    }

    twitching: boolean = false;

    //TODO goed testen, wat gebeurt er bij een twitch? Hoe kan ik een twitch herkennen?
    onTrackEvent(track: MediaStreamTrack, metadata?: JanusJS.RemoteTrackMetadata) {
        if (!this.stream) {
            this.stream = new MediaStream();
        }

        if (metadata && metadata.reason == 'created') {
            this.stream.addTrack(track);
        }

        if (this._state !== 'streaming') {
            return;
        }

        const twitching = this.stream.getVideoTracks().length === 0;

        if (twitching === this.twitching) {
            //same twitchyness, let's do nothing
            return;
        }

        if (twitching) {
            //transform the video to maintain the original aspect ratio
            this.addLog({ event: 'twitch-start' });
            this.onTwitchStart();
        } else {
            this.addLog({ event: 'twitch-end' });
            this.onTwitchEnd();
        }

        this.twitching = twitching;
    }

    onRoomMessage(message: JanusJS.Message, jsep?: JanusJS.JSEP) {
        const event: string = message['videoroom'];

        const { reject, resolve } = this._resolver || ({} as any);
        if (message.error && reject) {
            reject(message.error);
            this._resolver = undefined;
            return;
        }

        if (message.error) {
            this.onError(message.error_code);
            return;
        }

        switch (event) {
            case 'joined':
                if (resolve) {
                    if (message.publishers.length === 1) {
                        this._resolver = undefined;
                        resolve(message);
                    } else if (message.publishers.length > 1) {
                        reject('Too many publishers? That should not be the case');
                    } else {
                        //WARNING: side-effect!
                        this.room = message as Room;
                        this.addLog({
                            event: 'roomJoined',
                            message: 'nopublishers yet'
                        });
                    }
                }
                break;
            case 'destroyed':
                this.addLog({ event });
                //the room don't exist anymore...
                //now destroying will actually destroy Janus
                this.room = undefined;
                this.destroy();
                break;
            case 'hangup':
                //well well well, what will we do then?
                this.addLog({ event });
                this.destroy();
                break;
            case 'event':
                if (message['leaving'] === 'ok') {
                    this.addLog({ event: 'roomLeft' });
                    this.janus!.destroy({ unload: true });
                } else if (message['unpublished'] === 'ok') {
                    this.addLog({
                        ...message,
                        ...{ event: 'roomUnpublished' }
                    });
                } else if (message['room'] === this.streamName && 'publishers' in message) {
                    if (resolve) {
                        this._resolver = undefined;
                        //WARNING: SIDE EFFECT!
                        if (this.room) this.room.publishers.push(message.publishers[0]);
                        resolve(this.room);
                    }
                } else {
                    this.addLog({
                        ...{ event: 'UnhandledRoomMessageEvent' },
                        ...message
                    });
                }
                break;
            default:
                this.addLog({
                    ...{ event: 'UnhandledRoomMessage' },
                    ...message
                });
        }
    }

    onPublisherMessage(message: JanusJS.Message, jsep?: JanusJS.JSEP) {
        const { resolve, reject } = this._resolver || ({} as any);
        if (message.error && reject) {
            reject(message.error);
            this._resolver = undefined;
            return;
        }
        if (message.error) {
            this.onError(message.error);
            return;
        }

        const event = message['videoroom'];
        switch (event) {
            case 'attached': {
                if (resolve) resolve(jsep);
                this._resolver = undefined;
                break;
            }
            case 'event': {
                if (message['started'] === 'ok') {
                    if (resolve) resolve();
                    this._resolver = undefined;
                } else {
                    this.addLog({
                        ...{ event: 'UnhandledPublishMessage' },
                        ...message
                    });
                }
                break;
            }

            default:
                this.addLog({
                    ...{ event: 'UnhandledPublishMessage' },
                    ...message
                });
        }
    }

    get state(): string {
        return this._state;
    }
    set state(value: string) {
        if (!this.isStateChangeValid(value)) {
            throw new Error(`invalid state change from ${this._state} to ${value}`);
        }

        this._state = value;
        this.onStateChange(value);
    }

    //disconnected is always alowed
    //otherwise, the order of states should be obeyed
    private isStateChangeValid(newState: string): boolean {
        if (newState === 'destroying_janus') {
            return true;
        }

        const next = Player.states.indexOf(newState);
        if (next === -1) {
            throw new Error(`who knows about ${newState} antway??`);
        }

        const current = Player.states.indexOf(this._state);

        return next - current === 1;
    }

    static states = ['constructing', 'initializing', 'connecting', 'connected', 'attaching_room', 'joining_room', 'attaching_publisher', 'joining_publisher', 'answering', 'starting_feed', 'awaiting_connection_states', 'streaming', 'destroying_janus'];

    private _resolver: { resolve?: Function; reject?: Function } | undefined = undefined;
}
